/* MOG MINTS, the hand-offs (Ashot, 2026-09-15, his pick of variant B, applied to the product
   section first, then to the story and the FAQ the same day).

   What this does: a section marked data-handoff scrolls normally until its last screen is in
   view, then freezes; the section after it slides up over it, and the frozen section's content
   lifts and dims. The sticky element is Shopify's section WRAPPER, top -(height - viewport).
   --ho is 0 at the freeze and 1 once the next section covers the viewport.

   ⚠ DESKTOP ONLY SINCE 2026-09-18. Ashot: "Remove that scrolling logic from the mobile version
   entirely." Below 900px, the theme's own breakpoint, nothing here runs: no sticky wrapper, no
   runway, no lift, no dim, and no scroll work. Sections stack and scroll plainly.

   THE BREAKPOINT IS CROSSED, NOT JUST READ AT LOAD. A phone rotating and a desktop window being
   dragged narrow both cross 900px after this file has already set inline styles, so apply()
   tears those down rather than leaving a wrapper stuck sticky with a stale top. Crossing back
   rebuilds it. base.css carries a matching max-width:900px block that neutralises all four
   effects on its own, so the off state does not depend on this file running at all.

   Reduced motion: the whole file returns early, as before.

   THE DWELL. Without it the next section's top edge sits at exactly the viewport bottom at the
   instant the section freezes, so the cover starts on the very next pixel of scroll. For a
   section a viewport tall or shorter the freeze happens as the section's top reaches the
   viewport top, so the cover begins immediately and the section is never seen whole and still.
   The fix is a scroll RUNWAY: RUN viewport heights of padding on the sticky wrapper, below the
   section. Sticky pins where the wrapper PAINTS; it does not change where its siblings sit, so
   the next section's flow position moves down by the runway while the frozen section stays put.
   Nothing about the section's own layout or visual height changes.

   The runway is padding, so it paints nothing on its own. --ho-ground carries the section's own
   ground colour onto the wrapper, which only matters when the section is shorter than the
   viewport and the runway is therefore inside the viewport during the dwell. */
(function () {
  if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* THE ONE KNOB. The dwell, in viewport heights. The cover after it costs exactly 1.00, so this
     is the ratio of hold to cover. 0.60 was three fifths of the cover (decision 72, 2026-09-18).
     0 SINCE 2026-10-15 (decision 130), across the site at Ashot's word: "Fix the scroll mechanics we just fixed on the shop
     page across all site." On the shop page the 0.60 runway held the page still for 540px of scroll at 1440 by 900 before
     anything slid, the "slight delay" he felt; with none the next section starts over a frozen one on the first pixel after
     its last screen is in view. [data-handoff]{min-height:100svh} (base.css) still makes every frozen section at least a
     screen tall, so it is seen whole at the freeze. A per-section value (data-handoff="0.9") still overrides this. */
  var RUN = 0;

  /* 900px is the theme's mobile breakpoint: where the nav collapses to the drawer and the hero
     stacks. innerWidth is the fallback for a browser without matchMedia, which also gets no
     change events, which is why base.css and not this file is the authority on the off state. */
  var mqm = window.matchMedia ? matchMedia('(max-width: 900px)') : null;
  function mobile() { return mqm ? mqm.matches : innerWidth <= 900; }

  function clamp01(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }

  /* the ground the runway paints. Every .sheet carries a solid background-color, .prod carries
     var(--black), and .hero's ground is a gradient on .hero__stock whose shorthand resets
     background-color to transparent, which is why .hero gets one background-color declaration in
     base.css alongside this. If neither resolves the runway stays transparent, which is only
     ever visible on a section shorter than the viewport. */
  function ground(sec) {
    var probe = [sec, sec.querySelector('.sheet__stock,.hero__stock')], i, c;
    for (i = 0; i < probe.length; i++) {
      if (!probe[i]) continue;
      c = getComputedStyle(probe[i]).backgroundColor;
      if (c && c !== 'transparent' && c.replace(/\s/g, '') !== 'rgba(0,0,0,0)') return c;
    }
    return '';
  }

  [].slice.call(document.querySelectorAll('[data-handoff]')).forEach(function (sec) {
    /* the wrapper is Shopify's section element, not whatever the section's direct parent is: on the
       product page the section sits inside the product form, whose next siblings are the hidden
       inputs Shopify appends, and treating those as "next" froze the section lifted on load. */
    var wrap = sec.closest('.shopify-section') || sec.parentElement, next = wrap && wrap.nextElementSibling;
    if (wrap && !next && wrap.parentElement) { wrap = wrap.parentElement; next = wrap.nextElementSibling; }
    if (!wrap || !next) return;

    /* read once, used only when the hand-off is actually on. Cheap, and it has to happen before
       the wrapper takes a background of its own. */
    var g = ground(sec);

    /* per-section override: data-handoff="0.9" is 0.9 viewport heights of dwell on that one
       section. A bare data-handoff gives "" , parseFloat is NaN, and the default stands. */
    var r = parseFloat(sec.dataset.handoff);
    if (!(r >= 0)) r = RUN;

    /* cached in fit(), read in tick(). Reading wrap.offsetHeight inside the scroll handler forces
       a synchronous layout on every scroll event on every hand-off section, three of them on the
       home page. Nothing about the geometry changes between resizes. */
    var H = 0, travel = 0, cbh = null;

    function fit() {
      if (mobile()) return;
      var vh = innerHeight;
      /* the runway is written in svh, not in px from innerHeight, so a phone retracting its URL
         bar does not resize the page under the reader's thumb. */
      wrap.style.setProperty('--ho-run', (r * 100).toFixed(3));
      /* H is the section's painted height, never the runway. The runway is PADDING, so the
         wrapper's content box is still exactly the section, and the observer below hands that
         over fractionally and exactly. Two measurements were tried and both drifted:
         offsetHeight is rounded to an integer and left 0.40px in the sticky top, and
         rect.height minus getComputedStyle's padding string left 0.01px, because Chrome
         serialises 557.390625px as "557.4px". The bootstrap line below is only used for the
         first frame, before the observer has reported. */
      H = (cbh !== null) ? cbh
        : wrap.getBoundingClientRect().height - (parseFloat(getComputedStyle(wrap).paddingBottom) || 0);
      wrap.style.top = (H > vh ? -(H - vh) : 0) + 'px';
      /* a section shorter than the viewport freezes with its top at 0, so its travel is its own height */
      travel = Math.min(H, vh) || vh;
    }

    function tick() {
      if (mobile()) return;
      var p = clamp01(1 - next.getBoundingClientRect().top / travel);
      sec.style.setProperty('--ho', p.toFixed(4));
      /* nothing in the theme reads this attribute. Kept for the harness readout, behind a flag,
         so the store does not pay for it on every scroll event. */
      if (window.MOG_HO_DEBUG) sec.dataset.ho = p.toFixed(3);
    }

    /* the single place that decides whether this section is handed off at all. Every inline
       property this file writes is removed on the way out, so a wrapper cannot be left sticky
       with a stale negative top after a rotation. --ho goes back to its CSS fallback of 0, which
       is the resting state: no lift, full opacity. */
    function apply() {
      if (mobile()) {
        wrap.classList.remove('ho-stick');
        wrap.style.removeProperty('top');
        wrap.style.removeProperty('--ho-run');
        wrap.style.removeProperty('--ho-ground');
        sec.style.removeProperty('--ho');
        if (sec.dataset.ho) delete sec.dataset.ho;
        return;
      }
      wrap.classList.add('ho-stick');
      if (g) wrap.style.setProperty('--ho-ground', g);
      fit(); tick();
    }

    if (window.ResizeObserver) new ResizeObserver(function (e) {
      /* contentRect is the wrapper MINUS our runway padding, which is the section itself */
      cbh = e[0].contentRect.height; fit(); tick();
    }).observe(wrap);

    apply();
    addEventListener('scroll', tick, { passive: true });
    addEventListener('resize', function () { fit(); tick(); });
    /* same listener shape nav.js uses, for the same Safari reason */
    if (mqm && mqm.addEventListener) mqm.addEventListener('change', apply);
    else if (mqm && mqm.addListener) mqm.addListener(apply);
  });
})();
